import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Heart,
    Sparkles,
    ChevronRight,
    ChevronDown,
    Home,
    RefreshCw,
    Layers,
    ArrowDown
} from 'lucide-react';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { ModuleHeader } from '../components/brand/QuickExit';
import { PersistentBottomBar, BottomBarSpacer } from '../components/brand/PersistentBottomBar';

/**
 * Making Sense of Your Emotions
 * 
 * Based on the concept of Primary vs Secondary Emotions:
 * - Secondary emotions (surface): anger, guilt, frustration, overwhelm, worry, irritability, anxiety
 * - Primary emotions (deeper): fear, sadness, pain, grief, joy
 * 
 * The tool helps users:
 * 1. Identify the secondary emotion they're experiencing
 * 2. Understand there's likely a deeper primary emotion underneath
 * 3. Guide them to tap into that primary emotion
 * 4. Help them feel and process the primary emotion
 */

// Secondary emotions with their underlying primary emotions
const SECONDARY_EMOTIONS = [
    {
        id: 'anger',
        name: 'Anger',
        color: '#C75B5B',
        description: 'Feeling mad, furious, or wanting to lash out',
        example: '"I\'m so angry at them for what they did!"',
        primaryEmotions: ['fear', 'pain', 'grief'],
        insight: 'Anger often masks fear—fear of losing control, being disrespected, or not being heard. It can also cover deep pain or grief.',
        reframe: 'What am I really afraid of here? What pain might be underneath this anger?'
    },
    {
        id: 'frustration',
        name: 'Frustration',
        color: '#D4845C',
        description: 'Feeling stuck, blocked, or like nothing is working',
        example: '"Why won\'t this work? I\'ve tried everything!"',
        primaryEmotions: ['grief', 'fear', 'sadness'],
        insight: 'Frustration often hides grief—grieving what we expected, what we hoped for, or what we feel we\'ve lost. There may also be fear of failure.',
        reframe: 'What am I grieving here? What did I hope would happen that hasn\'t?'
    },
    {
        id: 'guilt',
        name: 'Guilt',
        color: '#8B7355',
        description: 'Feeling bad about something you did or didn\'t do',
        example: '"I should have done better. I\'m such a bad person."',
        primaryEmotions: ['pain', 'sadness', 'fear'],
        insight: 'Guilt often covers deep pain—pain about hurting someone, pain about not meeting our own standards. Underneath may be sadness or fear of rejection.',
        reframe: 'What pain am I carrying? What do I wish had been different?'
    },
    {
        id: 'anxiety',
        name: 'Anxiety',
        color: '#7A8B6E',
        description: 'Feeling worried, on edge, or like something bad might happen',
        example: '"What if everything goes wrong? I can\'t stop thinking about it."',
        primaryEmotions: ['fear'],
        insight: 'Anxiety is almost always rooted in fear—fear of the unknown, fear of not being good enough, fear of loss, or fear of harm.',
        reframe: 'What am I really afraid of? What\'s the worst that could happen, and how would I cope?'
    },
    {
        id: 'overwhelm',
        name: 'Overwhelm',
        color: '#7D8E95',
        description: 'Feeling like it\'s all too much, wanting to shut down',
        example: '"I can\'t handle this. There\'s too much going on."',
        primaryEmotions: ['fear', 'grief', 'sadness'],
        insight: 'Overwhelm often masks fear of not being able to cope, or grief about the life we wish we had. There may also be deep sadness about feeling alone in it.',
        reframe: 'What feels most scary right now? What do I need that I\'m not getting?'
    },
    {
        id: 'irritability',
        name: 'Irritability',
        color: '#7D8E95',
        description: 'Feeling snappy, easily annoyed, or on a short fuse',
        example: '"Everything and everyone is getting on my nerves!"',
        primaryEmotions: ['pain', 'sadness', 'fear'],
        insight: 'Irritability is often a sign of unmet needs—underneath there\'s usually pain, exhaustion, or sadness that hasn\'t been acknowledged.',
        reframe: 'What do I actually need right now? What\'s hurting that I haven\'t addressed?'
    },
    {
        id: 'worry',
        name: 'Worry',
        color: '#1D6B6B',
        description: 'Mind racing with "what ifs" and worst-case scenarios',
        example: '"But what if... and then what if... I can\'t stop thinking about it."',
        primaryEmotions: ['fear', 'grief'],
        insight: 'Worry is the mind\'s attempt to control fear. Underneath is usually fear of loss, fear of the unknown, or grief about something that\'s changing.',
        reframe: 'What am I trying to control by worrying? What loss am I afraid of?'
    }
];

// Primary emotions - the deeper feelings underneath
const PRIMARY_EMOTIONS = {
    fear: {
        name: 'Fear',
        color: '#7D8E95',
        description: 'A deep sense of threat, danger, or not being safe',
        howToFeel: 'Notice where fear lives in your body. Let yourself acknowledge: "I\'m scared." You don\'t have to fix it—just feel it.',
        release: 'Fear needs to be witnessed and named. Once felt, it often loosens its grip. Ask yourself: "Even if this fear came true, how would I cope?"',
        acceptance: 'It\'s okay to feel scared.'
    },
    sadness: {
        name: 'Sadness',
        color: '#7A8B9A',
        description: 'A tender ache, a heaviness, a longing for something',
        howToFeel: 'Let the sadness be there without rushing to fix it. It\'s okay to feel heavy. Sadness is love with nowhere to go.',
        release: 'Sadness needs time and tenderness. Let yourself feel it fully, and it will move through you. Tears are welcome here.',
        acceptance: 'It\'s okay to feel sad.'
    },
    pain: {
        name: 'Pain',
        color: '#7D8E95',
        description: 'A hurt that goes deep—emotional wounds that need attention',
        howToFeel: 'Acknowledge: "This hurts." Place a hand on your heart. The pain is real, and it makes sense that you feel it.',
        release: 'Pain needs compassion, not fixing. Speak to yourself as you would a dear friend who\'s hurting.',
        acceptance: 'It\'s okay to feel pain.'
    },
    grief: {
        name: 'Grief',
        color: '#7D8E95',
        description: 'Mourning a loss—of a person, a hope, a version of life, or ourselves',
        howToFeel: 'Grief comes in waves. Let it wash over you. You\'re mourning something that mattered. That\'s not weakness—it\'s love.',
        release: 'Grief doesn\'t have a timeline. Let yourself feel what you\'ve lost. The only way through grief is through it.',
        acceptance: 'It\'s okay to grieve.'
    },
    joy: {
        name: 'Joy',
        color: '#A8C4A0',
        description: 'A deep sense of gladness, aliveness, or gratitude',
        howToFeel: 'Joy can feel vulnerable. Let yourself receive it fully. You\'re allowed to feel good.',
        release: 'Joy doesn\'t need to be justified. Let it fill you without bracing for what might come next.',
        acceptance: 'It\'s okay to feel joy.'
    }
};

const EmotionIdentifier = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState('intro');
    const [selectedSecondary, setSelectedSecondary] = useState(null);
    const [selectedPrimary, setSelectedPrimary] = useState(null);
    const [expandedInfo, setExpandedInfo] = useState(false);

    const handleBack = () => {
        if (step === 'intro') {
            navigate('/home?tab=regulation');
        } else if (step === 'select') {
            setStep('intro');
        } else if (step === 'understand') {
            setStep('select');
            setSelectedSecondary(null);
        } else if (step === 'primary') {
            setStep('understand');
        } else if (step === 'feel') {
            setStep('primary');
            setSelectedPrimary(null);
        } else if (step === 'complete') {
            navigate('/home?tab=regulation');
        }
    };

    const getSelectedSecondaryData = () => {
        return SECONDARY_EMOTIONS.find(e => e.id === selectedSecondary);
    };

    const getPrimaryEmotionData = (primaryId) => {
        return PRIMARY_EMOTIONS[primaryId];
    };

    return (
        <div className="min-h-screen bg-transparent">
            {/* Module Header */}
            <ModuleHeader
                title="Making Sense of Your Emotions"
                onBack={handleBack}
                backDestination="/home?tab=regulation"
                exitDestination="/home?tab=regulation"
                showBack={step !== 'complete'}
            />

            <main className="max-w-xl mx-auto px-6 py-8">
                {/* INTRO - Explain the concept */}
                {step === 'intro' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="lavender" size="xl" useGradient={true} className="mx-auto mb-6">
                                <Layers className="w-8 h-8" />
                            </IconContainer>
                            
                            <Heading as="h1" size="text-2xl" className="mb-3">
                                What are you really feeling?
                            </Heading>
                            
                            <BodyText className="text-[var(--orenda-text-muted)] mb-6">
                                Sometimes powerful emotions like anger, frustration, or guilt aren't the whole story. 
                                Underneath, there's often a deeper feeling waiting to be seen.
                            </BodyText>
                        </div>

                        <div className="glass-card-sage rounded-2xl mb-6 p-5">
                            <div className="flex items-start gap-4">
                                <div 
                                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md"
                                    style={{ background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 100%)', color: 'white' }}
                                >
                                    <ArrowDown className="w-5 h-5" />
                                </div>
                                <div>
                                    <BodyText className="font-semibold text-[var(--orenda-text-primary)] mb-2">
                                        Surface vs. Source
                                    </BodyText>
                                    <SmallText className="text-[var(--orenda-text-muted)] block">
                                        We may act out in <span className="font-medium">anger</span>, but really, we're <span className="italic">afraid</span>. 
                                        We may sink into <span className="font-medium">guilt</span>, but really, we're in <span className="italic">pain</span>. 
                                        We may lose it in <span className="font-medium">frustration</span>, but really, we're <span className="italic">grieving</span>.
                                    </SmallText>
                                </div>
                            </div>
                        </div>

                        <div className="glass-card rounded-2xl mb-4 p-5">
                            <button 
                                onClick={() => setExpandedInfo(!expandedInfo)}
                                className="w-full flex items-center justify-between"
                            >
                                <BodyText className="font-medium text-[var(--orenda-text-primary)]">
                                    Why does this matter?
                                </BodyText>
                                <ChevronDown className={`w-5 h-5 text-[var(--orenda-text-muted)] transition-transform ${expandedInfo ? 'rotate-180' : ''}`} />
                            </button>
                            
                            {expandedInfo && (
                                <div className="mt-4 pt-4 border-t border-[var(--orenda-border)]">
                                    <SmallText className="text-[var(--orenda-text-secondary)] block mb-3">
                                        Expressing sadness or fear in front of others can feel vulnerable and weak. 
                                        Instead of acknowledging these primary emotions, we often unconsciously repress them.
                                    </SmallText>
                                    <SmallText className="text-[var(--orenda-text-secondary)] block mb-3">
                                        This can create <span className="font-medium">habitual anger</span>, <span className="font-medium">misunderstandings in relationships</span>, and <span className="font-medium">inner conflicts</span>.
                                    </SmallText>
                                    <SmallText className="text-[var(--orenda-text-secondary)] block mb-3">
                                        For example, if you're angry at your child for running into the street, staying angry damages the relationship. 
                                        But if you can tap into the <span className="italic">fear</span> underneath—"I was so scared when you did that"—you can connect from a more loving place.
                                    </SmallText>
                                    <SmallText className="text-[var(--orenda-text-secondary)] block">
                                        Understanding primary and secondary emotions may not fully resolve everything—but it will help you learn more about yourself and improve your emotional regulation.
                                    </SmallText>
                                </div>
                            )}
                        </div>

                        <div className="glass-card-lavender rounded-2xl mb-8 p-5">
                            <SmallText className="text-[var(--orenda-soft-teal)] font-medium block mb-2">
                                A gentle reminder:
                            </SmallText>
                            <SmallText className="text-[var(--orenda-text-secondary)] block">
                                The next time you feel a strong emotion, simply start by asking: <span className="italic">"Why do I feel this way? Is there something deeper underneath?"</span>
                            </SmallText>
                        </div>

                        <PersistentBottomBar>
                            <PrimaryButton 
                                onClick={() => setStep('select')} 
                                className="w-full" 
                                size="lg"
                                data-testid="start-btn"
                            >
                                <span>Let's explore what I'm feeling</span>
                                <ChevronRight className="w-5 h-5" />
                            </PrimaryButton>
                            <TertiaryButton 
                                onClick={() => navigate('/home?tab=regulation')} 
                                className="w-full mt-3"
                            >
                                Back to dashboard
                            </TertiaryButton>
                        </PersistentBottomBar>
                        <BottomBarSpacer />
                    </div>
                )}

                {/* SELECT - Choose secondary emotion */}
                {step === 'select' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-6">
                            <Heading as="h2" size="text-xl" className="mb-2">
                                What's showing up for you?
                            </Heading>
                            <BodyText className="text-[var(--orenda-text-muted)]">
                                These are often the emotions we notice first—the ones on the surface.
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-8">
                            {SECONDARY_EMOTIONS.map((emotion, index) => {
                                // Map emotions to glass card variants and gradient colors
                                const cardVariants = ['glass-card-rose', 'glass-card-gold', 'glass-card-sage', 'glass-card-lavender', 'glass-card', 'glass-card-rose', 'glass-card-sage'];
                                const iconGradients = [
                                    'linear-gradient(145deg, #E8A0A0 0%, #C97B7B 100%)', // Dusty Rose
                                    'linear-gradient(145deg, #FFBB98 0%, #E09060 100%)', // Coral
                                    'linear-gradient(145deg, #2A8585 0%, #1D6B6B 100%)', // Teal
                                    'linear-gradient(145deg, #E8A0A0 0%, #C97B7B 100%)', // Dusty Rose
                                    'linear-gradient(145deg, #9AABB8 0%, #7D8E95 100%)', // Neutral
                                    'linear-gradient(145deg, #FFBB98 0%, #E09060 100%)', // Coral
                                    'linear-gradient(145deg, #2A8585 0%, #1D6B6B 100%)', // Teal
                                ];
                                
                                return (
                                    <button 
                                        key={emotion.id}
                                        className={`w-full text-left rounded-2xl p-4 ${cardVariants[index % cardVariants.length]} card-hover transition-all ${
                                            selectedSecondary === emotion.id 
                                                ? 'ring-2 ring-[var(--orenda-soft-teal)]' 
                                                : ''
                                        }`}
                                        onClick={() => setSelectedSecondary(emotion.id)}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div 
                                                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md"
                                                style={{ background: iconGradients[index % iconGradients.length], color: 'white' }}
                                            >
                                                <span className="text-lg font-semibold">
                                                    {emotion.name.charAt(0)}
                                                </span>
                                            </div>
                                            <div className="flex-1">
                                                <BodyText className="font-semibold text-[var(--orenda-text-primary)]">
                                                    {emotion.name}
                                                </BodyText>
                                                <SmallText className="text-[var(--orenda-text-muted)]">
                                                    {emotion.description}
                                                </SmallText>
                                            </div>
                                            {selectedSecondary === emotion.id && (
                                                <div className="w-6 h-6 rounded-full bg-[var(--orenda-soft-teal)] flex items-center justify-center">
                                                    <span className="text-white text-sm">✓</span>
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <PersistentBottomBar>
                            <PrimaryButton 
                                onClick={() => setStep('understand')} 
                                className="w-full" 
                                size="lg"
                                disabled={!selectedSecondary}
                                data-testid="continue-btn"
                            >
                                <span>Continue</span>
                                <ChevronRight className="w-5 h-5" />
                            </PrimaryButton>
                        </PersistentBottomBar>
                        <BottomBarSpacer />
                    </div>
                )}

                {/* UNDERSTAND - Show what's underneath */}
                {step === 'understand' && selectedSecondary && (
                    <div className="animate-fade-in">
                        {(() => {
                            const emotion = getSelectedSecondaryData();
                            return (
                                <>
                                    <div className="text-center mb-6">
                                        <div 
                                            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                                            style={{ backgroundColor: `${emotion.color}20` }}
                                        >
                                            <span 
                                                className="text-2xl font-semibold"
                                                style={{ color: emotion.color }}
                                            >
                                                {emotion.name.charAt(0)}
                                            </span>
                                        </div>
                                        <Heading as="h2" size="text-xl" className="mb-2">
                                            You're feeling {emotion.name.toLowerCase()}
                                        </Heading>
                                        <BodyText className="text-[var(--orenda-text-muted)] italic">
                                            {emotion.example}
                                        </BodyText>
                                    </div>

                                    <div className="glass-card rounded-2xl p-5">
                                        <SmallText className="text-[var(--orenda-soft-teal)] font-medium block mb-2">
                                            What's really going on:
                                        </SmallText>
                                        <BodyText className="text-[var(--orenda-text-secondary)] mb-4">
                                            {emotion.insight}
                                        </BodyText>
                                        
                                        <div className="bg-[var(--orenda-border)]/30 rounded-lg p-4">
                                            <SmallText className="text-[var(--orenda-text-muted)] font-medium block mb-1">
                                                A question to sit with:
                                            </SmallText>
                                            <BodyText className="text-[var(--orenda-text-primary)] italic">
                                                "{emotion.reframe}"
                                            </BodyText>
                                        </div>
                                    </div>

                                    <div className="text-center mb-4">
                                        <SmallText className="text-[var(--orenda-text-muted)]">
                                            Underneath {emotion.name.toLowerCase()}, there's often:
                                        </SmallText>
                                    </div>

                                    <div className="flex justify-center gap-3 mb-8">
                                        {emotion.primaryEmotions.map((primaryId) => {
                                            const primary = getPrimaryEmotionData(primaryId);
                                            return (
                                                <div 
                                                    key={primaryId}
                                                    className="px-4 py-2 rounded-full text-sm font-medium"
                                                    style={{ 
                                                        backgroundColor: `${primary.color}15`,
                                                        color: primary.color
                                                    }}
                                                >
                                                    {primary.name}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <PersistentBottomBar>
                                        <PrimaryButton 
                                            onClick={() => setStep('primary')} 
                                            className="w-full" 
                                            size="lg"
                                            data-testid="go-deeper-btn"
                                        >
                                            <span>Go deeper</span>
                                            <ArrowDown className="w-5 h-5" />
                                        </PrimaryButton>
                                    </PersistentBottomBar>
                                    <BottomBarSpacer />
                                </>
                            );
                        })()}
                    </div>
                )}

                {/* PRIMARY - Choose the deeper emotion */}
                {step === 'primary' && selectedSecondary && (
                    <div className="animate-fade-in">
                        {(() => {
                            const secondaryEmotion = getSelectedSecondaryData();
                            return (
                                <>
                                    <div className="text-center mb-6">
                                        <Heading as="h2" size="text-xl" className="mb-2">
                                            What feels true underneath?
                                        </Heading>
                                        <BodyText className="text-[var(--orenda-text-muted)]">
                                            Which of these deeper feelings might be driving your {secondaryEmotion.name.toLowerCase()}?
                                        </BodyText>
                                    </div>

                                    <div className="space-y-3 mb-8">
                                        {secondaryEmotion.primaryEmotions.map((primaryId) => {
                                            const primary = getPrimaryEmotionData(primaryId);
                                            return (
                                                <div className="glass-card rounded-2xl p-5"
                                                    key={primaryId}
                                                    className={`p-4 cursor-pointer transition-all ${
                                                        selectedPrimary === primaryId 
                                                            ? 'ring-2 ring-[var(--orenda-soft-teal)] bg-[var(--orenda-soft-teal)]/5' 
                                                            : ''
                                                    }`}
                                                    onClick={() => setSelectedPrimary(primaryId)}
                                                    hoverable
                                                >
                                                    <div className="flex items-start gap-4">
                                                        <div 
                                                            className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                                                            style={{ backgroundColor: `${primary.color}15` }}
                                                        >
                                                            <span 
                                                                className="text-lg font-semibold"
                                                                style={{ color: primary.color }}
                                                            >
                                                                {primary.name.charAt(0)}
                                                            </span>
                                                        </div>
                                                        <div className="flex-1">
                                                            <BodyText className="font-semibold text-[var(--orenda-text-primary)] mb-1">
                                                                {primary.name}
                                                            </BodyText>
                                                            <SmallText className="text-[var(--orenda-text-muted)]">
                                                                {primary.description}
                                                            </SmallText>
                                                        </div>
                                                        {selectedPrimary === primaryId && (
                                                            <div className="w-6 h-6 rounded-full bg-[var(--orenda-soft-teal)] flex items-center justify-center flex-shrink-0">
                                                                <span className="text-white text-sm">✓</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <PersistentBottomBar>
                                        <PrimaryButton 
                                            onClick={() => setStep('feel')} 
                                            className="w-full" 
                                            size="lg"
                                            disabled={!selectedPrimary}
                                            data-testid="feel-it-btn"
                                        >
                                            <span>Help me feel this</span>
                                            <ChevronRight className="w-5 h-5" />
                                        </PrimaryButton>
                                    </PersistentBottomBar>
                                    <BottomBarSpacer />
                                </>
                            );
                        })()}
                    </div>
                )}

                {/* FEEL - Guidance for feeling the primary emotion */}
                {step === 'feel' && selectedPrimary && (
                    <div className="animate-fade-in">
                        {(() => {
                            const primary = getPrimaryEmotionData(selectedPrimary);
                            return (
                                <>
                                    <div className="text-center mb-6">
                                        <div 
                                            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                                            style={{ backgroundColor: `${primary.color}20` }}
                                        >
                                            <Heart 
                                                className="w-10 h-10"
                                                style={{ color: primary.color }}
                                            />
                                        </div>
                                        <Heading as="h2" size="text-xl" className="mb-2">
                                            Feeling your {primary.name.toLowerCase()}
                                        </Heading>
                                        <BodyText className="text-[var(--orenda-text-muted)]">
                                            This is the deeper truth. Let's sit with it.
                                        </BodyText>
                                    </div>

                                    <div className="glass-card rounded-2xl p-5">
                                        <SmallText className="text-[var(--orenda-soft-teal)] font-medium block mb-2">
                                            How to feel it:
                                        </SmallText>
                                        <BodyText className="text-[var(--orenda-text-secondary)]">
                                            {primary.howToFeel}
                                        </BodyText>
                                    </div>

                                    <div className="glass-card rounded-2xl p-5">
                                        <SmallText className="text-[var(--orenda-soft-teal)] font-medium block mb-2">
                                            How it releases:
                                        </SmallText>
                                        <BodyText className="text-[var(--orenda-text-secondary)]">
                                            {primary.release}
                                        </BodyText>
                                    </div>

                                    {/* Acceptance affirmation */}
                                    <div className="glass-card rounded-2xl p-5">
                                        <SmallText className="text-[var(--orenda-text-muted)] block mb-2">
                                            Try saying to yourself:
                                        </SmallText>
                                        <BodyText className="text-[var(--orenda-text-primary)] text-lg font-medium italic">
                                            "{primary.acceptance}"
                                        </BodyText>
                                    </div>

                                    {/* F.O.L. Technique */}
                                    <div className="glass-card rounded-2xl p-5">
                                        <SmallText className="text-[var(--orenda-text-muted)] font-medium block mb-3">
                                            If this emotion is driving difficult behaviour, try:
                                        </SmallText>
                                        
                                        <div className="space-y-4">
                                            <div className="flex items-start gap-3">
                                                <div className="w-8 h-8 rounded-full bg-[#1D6B6B]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                    <span className="text-[#1D6B6B] font-semibold text-sm">F</span>
                                                </div>
                                                <div>
                                                    <BodyText className="font-medium text-[var(--orenda-text-primary)] mb-1">
                                                        Fact-Check
                                                    </BodyText>
                                                    <SmallText className="text-[var(--orenda-text-muted)]">
                                                        Step back and differentiate between facts and assumptions. Are facts fueling this emotion, or are assumptions pushing you to act rashly?
                                                    </SmallText>
                                                </div>
                                            </div>

                                            <div className="flex items-start gap-3">
                                                <div className="w-8 h-8 rounded-full bg-[#7D8E95]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                    <span className="text-[#7D8E95] font-semibold text-sm">O</span>
                                                </div>
                                                <div>
                                                    <BodyText className="font-medium text-[var(--orenda-text-primary)] mb-1">
                                                        Opposite Urge
                                                    </BodyText>
                                                    <SmallText className="text-[var(--orenda-text-muted)]">
                                                        If your emotion is based on assumptions, try the opposite action. Instead of a defensive response, ask them to explain. Instead of withdrawing, reach out.
                                                    </SmallText>
                                                </div>
                                            </div>

                                            <div className="flex items-start gap-3">
                                                <div className="w-8 h-8 rounded-full bg-[#7D8E95]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                    <span className="text-[#7D8E95] font-semibold text-sm">L</span>
                                                </div>
                                                <div>
                                                    <BodyText className="font-medium text-[var(--orenda-text-primary)] mb-1">
                                                        Leave
                                                    </BodyText>
                                                    <SmallText className="text-[var(--orenda-text-muted)]">
                                                        When the intensity gets too high, remove yourself from the situation. Walk away and allow yourself to calm down before returning.
                                                    </SmallText>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="text-center mb-8">
                                        <BodyText className="text-[var(--orenda-text-muted)] italic">
                                            Take as long as you need here. There's no rush.
                                        </BodyText>
                                    </div>

                                    <PersistentBottomBar>
                                        <PrimaryButton 
                                            onClick={() => setStep('complete')} 
                                            className="w-full" 
                                            size="lg"
                                            data-testid="complete-btn"
                                        >
                                            <span>I'm ready to move on</span>
                                        </PrimaryButton>
                                        <SecondaryButton 
                                            onClick={() => navigate('/regulate/box-breathing')} 
                                            className="w-full mt-3"
                                        >
                                            <span>Try some breathing first</span>
                                        </SecondaryButton>
                                    </PersistentBottomBar>
                                    <BottomBarSpacer />
                                </>
                            );
                        })()}
                    </div>
                )}

                {/* COMPLETE */}
                {step === 'complete' && (
                    <div className="animate-fade-in text-center">
                        <IconContainer variant="sage" size="xl" useGradient={true} className="mx-auto mb-6">
                            <Sparkles className="w-8 h-8" />
                        </IconContainer>
                        
                        <Heading as="h2" size="text-2xl" className="mb-3">
                            Well done
                        </Heading>
                        
                        <BodyText className="text-[var(--orenda-text-muted)] mb-4">
                            You just did something powerful—you went beneath the surface and touched what's really there.
                        </BodyText>
                        
                        <BodyText className="text-[var(--orenda-text-secondary)] mb-8 italic">
                            "When we feel our deeper emotions, we can actually let them go. 
                            If we never identify them, we can't release them."
                        </BodyText>

                        <div className="space-y-3">
                            <PrimaryButton 
                                onClick={() => {
                                    setStep('intro');
                                    setSelectedSecondary(null);
                                    setSelectedPrimary(null);
                                    setExpandedInfo(false);
                                }} 
                                className="w-full" 
                                size="lg"
                                data-testid="explore-again-btn"
                            >
                                <RefreshCw className="w-5 h-5" />
                                <span>Explore another feeling</span>
                            </PrimaryButton>
                            
                            <SecondaryButton 
                                onClick={() => navigate('/home?tab=regulation')} 
                                className="w-full" 
                                size="lg"
                                data-testid="return-home-btn"
                            >
                                <Home className="w-5 h-5" />
                                <span>Back to Regulation Hub</span>
                            </SecondaryButton>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default EmotionIdentifier;
